import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Badge, Button, Empty, Spin, message } from 'antd';
import { useConfig } from '../../contexts/ConfigContext';
import { useCompany } from '../../contexts/CompanyContext';
import { useBotAdmin } from '../../contexts/BotAdminContext';
import { useConversationMessages, useConversations } from '../../hooks/useWpbot';
import useAuth from '../../hooks/useAuth';
import fetchDataService from '../../services/fetchDataService';
import BotSelector from './BotSelector';
import { TBotMessage, TConversation } from '../../models/wpbotModel';
import { formatDateTime } from '../../utils';
import { closedWindowMessage, replyWindow } from '../../utils/replyWindow';
import './ViewConversations.css';

/**
 * Bandeja del bot. La página reciente llega sola cada pocos segundos.
 * "Cargar mensajes anteriores" pide el tramo más viejo. Tomar el control
 * calla al agente y deja escribir por el mismo WhatsApp.
 */
const ViewConversations: React.FC = () => {
  const { config } = useConfig();
  const { company } = useCompany();
  const { botAdmin } = useBotAdmin();
  const { tokenData } = useAuth.useToken('authCsrfToken', config.session, config.key);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [older, setOlder] = useState<TBotMessage[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [handledOverride, setHandledOverride] = useState<'bot' | 'human' | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const stickToBottom = useRef(true);

  const botId = botAdmin.selectedBotId;
  const companyUid = company.current?.uid;
  const authBody = (extra: Record<string, unknown>) => ({
    ...extra,
    csrfToken: tokenData?.token?.uid,
    session: tokenData?.session,
    company: companyUid,
  });

  const { data, isLoading, error, mutate: mutateList } = useConversations(tokenData, {
    bot_id: botId, company: companyUid, limit: 100,
  });
  const {
    data: messagesData,
    isLoading: loadingMessages,
    error: messagesError,
    mutate: mutateMessages,
  } = useConversationMessages(tokenData, {
    conversation_id: selectedId, company: companyUid, limit: 40,
  });

  const conversations: TConversation[] = data?.status === 200 ? (data?.conversations || []) : [];
  const total: number = data?.status === 200 ? (data?.total ?? conversations.length) : 0;
  const selected = conversations.find((c) => c.id === selectedId) || null;
  const latest: TBotMessage[] = messagesData?.status === 200 ? (messagesData?.messages || []) : [];
  const messages = useMemo(() => mergeMessages(older, latest), [older, latest]);
  const handled: 'bot' | 'human' = handledOverride
    || (messagesData?.handled_by === 'human' ? 'human' : null)
    || (selected?.handled_by === 'human' ? 'human' : 'bot');
  const customerWindow = replyWindow(messagesData);
  const windowClosed = customerWindow.known && !customerWindow.open;
  const canType = handled === 'human' && !windowClosed;

  useEffect(() => {
    if (error) message.error('Error al cargar las conversaciones');
  }, [error]);

  useEffect(() => {
    if (messagesError) message.error('Error al cargar los mensajes de la conversación');
  }, [messagesError]);

  useEffect(() => {
    setSelectedId(null);
  }, [botId]);

  useEffect(() => {
    setOlder([]);
    setDraft('');
    setHandledOverride(null);
    stickToBottom.current = true;
  }, [selectedId]);

  useEffect(() => {
    if (messagesData?.status === 200 && older.length === 0) {
      setHasMore(Boolean(messagesData.has_more));
    }
  }, [messagesData, older.length]);

  useEffect(() => {
    if (!stickToBottom.current) return;
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, selectedId]);

  useEffect(() => {
    if (!selectedId || messagesData?.status !== 200) return;
    mutateList((current: any) => {
      if (!current?.conversations) return current;
      return {
        ...current,
        conversations: current.conversations.map((c: TConversation) =>
          c.id === selectedId ? { ...c, unread_count: 0 } : c
        ),
      };
    }, { revalidate: false });
  }, [messagesData, selectedId, mutateList]);

  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    stickToBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  };

  const loadOlder = async () => {
    const oldest = messages[0];
    if (!selectedId || !oldest || !tokenData?.key) return;
    setLoadingOlder(true);
    stickToBottom.current = false;
    try {
      const res = await fetchDataService.post('/wpbot/get-conversation-messages', tokenData.key, authBody({
        conversation_id: selectedId,
        before: oldest.created_at,
        limit: 40,
      }));
      if (res?.status !== 200) {
        message.error(res?.message || 'No se pudieron cargar mensajes anteriores');
        return;
      }
      setOlder((prev) => mergeMessages(res.messages || [], prev));
      setHasMore(Boolean(res.has_more));
    } catch {
      message.error('No se pudieron cargar mensajes anteriores');
    } finally {
      setLoadingOlder(false);
    }
  };

  const control = async (action: 'take' | 'send' | 'release', text = '') => {
    if (!selectedId || !tokenData?.key) return;
    if (action === 'send' && windowClosed) {
      message.warning(closedWindowMessage(customerWindow.lastInboundAt));
      return;
    }
    setBusy(true);
    try {
      const res = await fetchDataService.post('/wpbot/take-conversation', tokenData.key, authBody({
        conversation_id: selectedId,
        action,
        text,
      }));
      if (res?.status !== 200) {
        message.error(res?.message || 'No se pudo actualizar la conversación');
        return;
      }
      setHandledOverride(action === 'release' ? 'bot' : 'human');
      if (action === 'send') setDraft('');
      stickToBottom.current = true;
      await mutateMessages();
      await mutateList();
    } catch {
      message.error('No se pudo actualizar la conversación');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="conv-page">
      <div className="conv-top">
        <h2>Conversaciones</h2>
        <BotSelector />
      </div>
      <div className="conv-layout">
        <aside className="conv-list">
          <div className="conv-list-head">
            {botId ? `${total} conversación${total === 1 ? '' : 'es'}` : 'Selecciona un bot'}
          </div>
          <div className="conv-list-scroll">
            {isLoading && <div style={{ padding: 24, textAlign: 'center' }}><Spin /></div>}
            {!isLoading && conversations.length === 0 && (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={botId ? 'Sin conversaciones' : 'Selecciona un bot'} />
            )}
            {conversations.map((conv) => {
              const name = conv.contact_name || conv.user_phone;
              return (
                <button
                  key={conv.id}
                  type="button"
                  className={`conv-row${conv.id === selectedId ? ' is-active' : ''}`}
                  onClick={() => setSelectedId(conv.id)}
                >
                  <span className="conv-avatar is-in" aria-hidden>{initials(name)}</span>
                  <div className="conv-row-body">
                    <div className="conv-row-name">
                      <span className="conv-row-title">{name}</span>
                      <span className="conv-row-meta">
                        {when(conv.last_message_at)}
                        <Badge count={conv.unread_count || 0} overflowCount={99} color="#1677ff" />
                      </span>
                    </div>
                    <div className="conv-row-phone">
                      {conv.user_phone}
                      {conv.handled_by === 'human' ? <span className="conv-pill">En tus manos</span> : null}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </aside>

        <section className="conv-thread">
          {!selected && (
            <div className="conv-empty">Elige una conversación. Lo más reciente queda abajo.</div>
          )}
          {selected && (
            <>
              <header className="conv-thread-head">
                <div className="conv-thread-who">
                  <span className="conv-avatar is-in" aria-hidden>
                    {initials(selected.contact_name || selected.user_phone)}
                  </span>
                  <div>
                  <div className="conv-thread-title">{selected.contact_name || selected.user_phone}</div>
                  <div className="conv-thread-sub">
                    {selected.user_phone}
                    {' · '}
                    {handled === 'human'
                      ? 'Tú tienes el control. El bot vuelve si te quedas callado.'
                      : 'El bot responde.'}
                  </div>
                  </div>
                </div>
                <div className="conv-actions">
                  {handled === 'human' ? (
                    <Button disabled={busy} onClick={() => control('release')}>Devolver al bot</Button>
                  ) : (
                    <Button type="primary" disabled={busy} onClick={() => control('take')}>Tomar conversación</Button>
                  )}
                </div>
              </header>
              <div className="conv-scroll" ref={scrollRef} onScroll={onScroll}>
                {loadingMessages && messages.length === 0 && (
                  <div style={{ textAlign: 'center', marginTop: 40 }}><Spin /></div>
                )}
                {hasMore && (
                  <button type="button" className="conv-more" disabled={loadingOlder} onClick={loadOlder}>
                    {loadingOlder ? 'Cargando…' : 'Cargar mensajes anteriores'}
                  </button>
                )}
                {messages.map((msg, index) => {
                  const kind = messageKind(msg);
                  const who = kind === 'in'
                    ? (selected.contact_name || 'Contacto')
                    : (kind === 'staff' ? 'Tú' : 'Bot');
                  const prevKind = index > 0 ? messageKind(messages[index - 1]) : null;
                  const nextKind = index < messages.length - 1 ? messageKind(messages[index + 1]) : null;
                  const groupEnd = nextKind !== kind;
                  const avatar = groupEnd
                    ? <span className={`conv-avatar is-${kind}`} title={who}>{initials(who)}</span>
                    : <span className="conv-avatar is-spacer" aria-hidden />;
                  return (
                    <div
                      key={msg.id}
                      className={`conv-msg is-${kind}${prevKind !== kind ? ' is-head' : ''}${groupEnd ? ' is-tail' : ''}`}
                    >
                      {kind === 'in' ? avatar : null}
                      <div className={`conv-bubble is-${kind}`}>
                      <div className="conv-text">
                        {renderWhatsAppText(
                          parseServiceOptions(msg.content).length
                            ? stripNumberedOptions(msg.content)
                            : msg.content
                        )}
                      </div>
                      {parseServiceOptions(msg.content).length > 0 && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
                          {parseServiceOptions(msg.content).map((opt) => (
                            <div key={opt.n} style={{ background: '#fff', borderRadius: 8, padding: '6px 10px', fontSize: 13 }}>
                              {opt.label}
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="conv-time">{formatDateTime(msg.created_at)}</div>
                      </div>
                      {kind === 'in' ? null : avatar}
                    </div>
                  );
                })}
              </div>
              {windowClosed && (
                <div className="conv-window-alert" role="status">
                  {closedWindowMessage(customerWindow.lastInboundAt)}
                </div>
              )}
              <form
                className="conv-compose"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (!draft.trim() || busy || !canType) return;
                  control('send', draft.trim());
                }}
              >
                <textarea
                  rows={2}
                  value={draft}
                  disabled={!canType || busy}
                  placeholder={
                    windowClosed
                      ? 'WhatsApp no entrega mensajes hasta que el cliente escriba'
                      : handled === 'human'
                        ? 'Escribe por WhatsApp…'
                        : 'Toma la conversación para escribir'
                  }
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      if (draft.trim() && !busy && canType) control('send', draft.trim());
                    }
                  }}
                />
                <button className="conv-send" type="submit" disabled={!canType || busy || !draft.trim()}>
                  Enviar
                </button>
              </form>
            </>
          )}
        </section>
      </div>
    </div>
  );
};

function messageKind(msg: TBotMessage): 'in' | 'staff' | 'bot' {
  if (msg.direction === 'in') return 'in';
  return msg.sender === 'staff' ? 'staff' : 'bot';
}

function initials(label: string): string {
  const clean = String(label || '').trim();
  const words = clean.split(/\s+/).filter((word) => /[A-Za-zÁÉÍÓÚÜÑáéíóúüñ]/.test(word));
  if (words.length >= 2) return (words[0][0] + words[words.length - 1][0]).toLocaleUpperCase('es');
  if (words.length === 1) return words[0].slice(0, 2).toLocaleUpperCase('es');
  const digits = clean.replace(/\D/g, '');
  return digits.slice(-2) || '?';
}

function mergeMessages(a: TBotMessage[], b: TBotMessage[]): TBotMessage[] {
  const byId = new Map<string, TBotMessage>();
  [...a, ...b].forEach((msg) => {
    if (msg?.id) byId.set(msg.id, msg);
  });
  return Array.from(byId.values()).sort((x, y) => String(x.created_at).localeCompare(String(y.created_at)));
}

function when(iso?: string) {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return formatDateTime(iso);
  const now = new Date();
  const sameDay = date.toDateString() === now.toDateString();
  const time = date.toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' });
  if (sameDay) return `Hoy ${time}`;
  return formatDateTime(iso);
}

function renderWhatsAppText(text: string) {
  const parts = String(text || '').split(/(\*\*[^*\n]+\*\*|\*[^*\n]+\*)/g);
  return parts.map((part, i) => {
    const m = part.match(/^\*\*([^*\n]+)\*\*$/) || part.match(/^\*([^*\n]+)\*$/);
    if (m) return <strong key={i}>{m[1]}</strong>;
    return <span key={i}>{part}</span>;
  });
}

function stripNumberedOptions(text: string) {
  return String(text || '')
    .split('\n')
    .filter((line) => !/^\s*\d+[.)]\s+/.test(line))
    .join('\n')
    .trim();
}

function parseServiceOptions(text: string): { n: string; label: string }[] {
  const lines = String(text || '').split('\n');
  const out: { n: string; label: string }[] = [];
  for (const line of lines) {
    const m = line.trim().match(/^(\d+)[.)]\s+\*?\*?(.+?)\*?\*?\s*$/);
    if (m) out.push({ n: m[1], label: m[2].replace(/\*/g, '').trim() });
  }
  return out.length >= 2 ? out : [];
}

export default ViewConversations;
